import { del, get, post } from '../api/axios';
import endpoints from '../api/endpoints';
import { getUserAvatarUrl } from './userAvatar';
import { getUserFullName, nameWithYou } from './userDisplay';

const normalizeUserIds = (userIds = []) =>
  (Array.isArray(userIds) ? userIds : [])
    .map(id => (id == null ? '' : String(id).trim()))
    .filter(Boolean);

const getCurrentUserDisplayName = (user) =>
  getUserFullName(user, { fallback: 'You' });

const isProjectMorphType = (value = '') => {
  const normalized = String(value).toLowerCase().replace(/[\s-]+/g, '_');
  // Exact project morph only — do NOT match "project_task"
  return (
    normalized === 'project' ||
    normalized.endsWith('\\project') ||
    normalized.endsWith('/project') ||
    normalized.endsWith('models\\project') ||
    normalized.endsWith('models/project')
  );
};

const isTaskMorphType = (value = '') => {
  const normalized = String(value).toLowerCase().replace(/[\s-]+/g, '_');
  return (
    normalized === 'task' ||
    normalized === 'project_task' ||
    normalized.endsWith('\\task') ||
    normalized.endsWith('/task') ||
    normalized.endsWith('project_task')
  );
};

const isCityMorphType = (value = '') => {
  const normalized = String(value).toLowerCase().replace(/[\s-]+/g, '_');
  return (
    normalized === 'city' ||
    normalized.endsWith('\\city') ||
    normalized.endsWith('/city') ||
    normalized.endsWith('models\\city') ||
    normalized.endsWith('models/city')
  );
};

const asProjectRef = (value, name = '') => {
  if (!value) return null;
  if (typeof value === 'string' || typeof value === 'number') {
    return { id: String(value), name: name || '' };
  }
  if (typeof value === 'object' && value.id) {
    return {
      id: String(value.id),
      name: value.name || value.title || name || '',
    };
  }
  return null;
};

/**
 * Resolve the parent project for a session.
 *
 * API shapes:
 * - source_type/scope "project": targetable is the project (id + name)
 * - source_type/scope "task": targetable is the task; project id lives on
 *   targetable.project_id and/or targets[].meta.project_id
 *
 * Never use a task's targetable.id as projectId (GET /projects/:id → 404).
 */
export const resolveSessionProject = (session) => {
  if (!session) return null;

  const direct = asProjectRef(session.project) || asProjectRef(session.project_id);
  if (direct) return direct;

  const targets = Array.isArray(session.targets) ? session.targets : [];
  for (const target of targets) {
    const targetable = target?.targetable;
    const typeHint = String(
      target?.targetable_type || target?.scope || target?.type || '',
    ).toLowerCase();

    // Task-scoped: project id is on meta / targetable.project_id only
    if (isTaskMorphType(typeHint) || session.source_type === 'task') {
      const fromTask =
        asProjectRef(target?.meta?.project_id, targetable?.title) ||
        asProjectRef(targetable?.project_id, targetable?.title) ||
        asProjectRef(targetable?.project);
      if (fromTask) return fromTask;
      continue;
    }

    // Project-scoped: targetable itself is the project
    if (isProjectMorphType(typeHint) || target?.scope === 'project') {
      const project =
        asProjectRef(targetable) ||
        asProjectRef(target?.targetable_id) ||
        asProjectRef(target?.meta?.project_id);
      if (project) return project;
      continue;
    }

    // City-scoped: never treat the city targetable as a project.
    if (
      isCityMorphType(typeHint) ||
      target?.scope === 'city' ||
      String(session.session_target || session.source_type || '').toLowerCase() ===
        'city'
    ) {
      continue;
    }

    // Unknown morph — prefer explicit project_id fields, never bare task-like ids
    const nested =
      asProjectRef(target?.meta?.project_id, targetable?.title) ||
      asProjectRef(targetable?.project) ||
      asProjectRef(targetable?.project_id, targetable?.title);
    if (nested) return nested;
  }

  return null;
};

const asCityRef = (value, name = '') => {
  if (!value) return null;
  if (typeof value === 'string' || typeof value === 'number') {
    return { id: String(value), name: name || '' };
  }
  if (typeof value === 'object' && value.id) {
    const cityName =
      value.name ||
      (value.city && value.state ? `${value.city}, ${value.state}` : '') ||
      value.city ||
      value.shortName ||
      name ||
      '';
    return {
      id: String(value.id),
      name: cityName,
    };
  }
  return null;
};

/**
 * Resolve the parent city for a City Hub / shadow-working session.
 * API: session_target "city", targets[].targetable is the city.
 */
export const resolveSessionCity = (session) => {
  if (!session) return null;

  const direct = asCityRef(session.city) || asCityRef(session.city_id);
  if (direct) return direct;

  const targets = Array.isArray(session.targets) ? session.targets : [];
  for (const target of targets) {
    const targetable = target?.targetable;
    const typeHint = String(
      target?.targetable_type || target?.scope || target?.type || '',
    ).toLowerCase();

    if (
      isCityMorphType(typeHint) ||
      target?.scope === 'city' ||
      String(session.session_target || session.source_type || '').toLowerCase() ===
        'city'
    ) {
      const city =
        asCityRef(targetable) ||
        asCityRef(target?.targetable_id, targetable?.name || targetable?.city) ||
        asCityRef(target?.meta?.city_id, targetable?.name);
      if (city) return city;
    }
  }

  if (
    String(session.session_target || session.source_type || '').toLowerCase() ===
    'city'
  ) {
    const firstId = Array.isArray(session.target_ids)
      ? session.target_ids[0]
      : null;
    return asCityRef(firstId);
  }

  return null;
};

export const resolveSessionProjectId = (session) =>
  resolveSessionProject(session)?.id || null;

export const resolveSessionCityId = (session) =>
  resolveSessionCity(session)?.id || null;

export const toSessionCityPayload = city => {
  if (!city?.id) return { cityId: null, city: null }
  const name = city.name || city.shortName || city.title || ''
  return {
    cityId: String(city.id),
    city: {
      id: String(city.id),
      name,
      shortName: city.shortName || name,
      title: name,
    },
  }
}

/** Label under the session title (project name, city name, else task title). */
export const resolveSessionSubtitle = (session) => {
  const project = resolveSessionProject(session);
  if (project?.name) return project.name;

  const city = resolveSessionCity(session);
  if (city?.name) return city.name;

  const target = session?.targets?.[0];
  const targetable = target?.targetable;
  return (
    targetable?.name ||
    targetable?.title ||
    targetable?.city ||
    session?.title ||
    session?.working_note ||
    ''
  );
};

export const getProjectCollaborators = (
  project,
  currentUser,
  { excludeCurrentUser = false } = {}
) => {
  const currentUserId = currentUser?.id != null ? String(currentUser.id) : '';
  const list = project?.collaborators || project?.members || [];
  const people = list
    .map((person) => {
      const user = person.user || {};
      const userId = person.userId || person.user_id || user.id || null;
      if (!userId) return null;
      const id = String(userId);
      const name =
        person.name ||
        `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
        user.full_name ||
        user.email ||
        'Collaborator';
      return {
        id,
        userId: id,
        name: nameWithYou(name, Boolean(currentUserId && id === currentUserId)),
        email: user.email || person.email || '',
        role:
          person.role ||
          person.role_types?.[0]?.public_name ||
          person.role_types?.[0]?.name ||
          'Member',
        avatar:
          person.avatar ||
          person.src ||
          getUserAvatarUrl(user) ||
          getUserAvatarUrl(person) ||
          null,
        isYou: Boolean(currentUserId && id === currentUserId),
      };
    })
    .filter(Boolean);

  if (
    !excludeCurrentUser &&
    currentUserId &&
    !people.some((person) => person.isYou)
  ) {
    people.unshift({
      id: currentUserId,
      userId: currentUserId,
      name: nameWithYou(getCurrentUserDisplayName(currentUser), true),
      email: currentUser?.email || '',
      role: 'Member',
      avatar: getUserAvatarUrl(currentUser),
      isYou: true,
    });
  } else if (!excludeCurrentUser) {
    people.sort((a, b) => Number(b.isYou) - Number(a.isYou));
  }

  if (excludeCurrentUser && currentUserId) {
    return people.filter((person) => !person.isYou && person.id !== currentUserId);
  }

  return people;
};

export const buildSessionPayload = ({
  type,
  projectId,
  note,
  userIds = [],
  sessionTarget,
  targetIds,
  title,
}) => {
  const workingNote = (note || '').trim();
  const resolvedTarget = sessionTarget === 'task' ? 'task' : 'project';
  const resolvedIds = (Array.isArray(targetIds) ? targetIds : [])
    .map((id) => (id == null ? '' : String(id)))
    .filter(Boolean);

  const payload = {
    type,
    visibility_type: 'private',
    working_note: workingNote,
    session_target: resolvedTarget,
    target_ids:
      resolvedIds.length > 0
        ? resolvedIds
        : projectId
          ? [String(projectId)]
          : [],
    user_ids: userIds,
  };

  const sessionTitle = String(title || '').trim();
  if (resolvedTarget === 'task' && sessionTitle) {
    payload.title = sessionTitle;
  } else if (type === 'solo') {
    payload.title = workingNote || 'Solo Session';
  }

  return payload;
};

export const createProjectSession = async ({
  type,
  projectId,
  note,
  userIds = [],
  sessionTarget,
  targetIds,
  title,
}) => {
  const workingNote = String(note || '').trim();
  if (!workingNote) {
    return { ok: false, reason: 'empty_note' };
  }

  const response = await post(
    endpoints.createSession,
    buildSessionPayload({
      type,
      projectId,
      note: workingNote,
      userIds,
      sessionTarget,
      targetIds,
      title,
    })
  );

  if (!response?.data?.success) {
    return { ok: false, reason: 'api', response };
  }

  return { ok: true, created: response.data.result, response };
};

export const joinProjectSession = async ({ sessionId, note }) => {
  const workingNote = String(note || '').trim();
  if (!workingNote) {
    return { ok: false, reason: 'empty_note' };
  }

  const id = String(sessionId || '').trim();
  if (!id) {
    return { ok: false, reason: 'missing_session' };
  }

  const response = await post(endpoints.joinSession(id), {
    working_note: workingNote,
  });

  if (!response?.data?.success) {
    return { ok: false, reason: 'api', response };
  }

  return { ok: true, joined: response.data.result, response };
};

export const buildCitySessionPayload = ({
  title,
  note,
  cityId,
  userIds = [],
  visibility = 'global',
}) => {
  const workingNote = String(note || '').trim();
  const sessionTitle = String(title || '').trim() || workingNote || 'Shadow Session';
  const visibilityType =
    String(visibility || 'global').toLowerCase() === 'private'
      ? 'private'
      : 'global';
  const ids =
    visibilityType === 'private'
      ? (Array.isArray(userIds) ? userIds : [])
          .map(id => (id == null ? '' : String(id).trim()))
          .filter(Boolean)
      : [];

  return {
    type: 'shadow',
    visibility_type: visibilityType,
    title: sessionTitle,
    working_note: workingNote,
    session_target: 'city',
    target_ids: [cityId],
    user_ids: ids,
  };
};

export const createCitySession = async ({
  title,
  note,
  cityId,
  userIds = [],
  visibility = 'global',
}) => {
  const workingNote = String(note || '').trim();
  if (!cityId) {
    return { ok: false, reason: 'empty_city' };
  }
  if (!workingNote) {
    return { ok: false, reason: 'empty_note' };
  }

  const visibilityType =
    String(visibility || 'global').toLowerCase() === 'private'
      ? 'private'
      : 'global';
  if (
    visibilityType === 'private' &&
    !(Array.isArray(userIds) && userIds.some(id => String(id || '').trim()))
  ) {
    return { ok: false, reason: 'empty_collaborators' };
  }

  const response = await post(
    endpoints.createSession,
    buildCitySessionPayload({
      title,
      note: workingNote,
      cityId,
      userIds,
      visibility: visibilityType,
    })
  );

  if (!response?.data?.success) {
    return { ok: false, reason: 'api', response };
  }

  return { ok: true, created: response.data.result, response };
};

export const joinSession = async (sessionId, { note } = {}) => {
  if (!sessionId) {
    return { ok: false, reason: 'empty_session' };
  }

  const payload = {};
  const workingNote = String(note || '').trim();
  if (workingNote) payload.working_note = workingNote;

  const response = await post(endpoints.joinSession(sessionId), payload);

  if (!response?.data?.success) {
    return { ok: false, reason: 'api', response };
  }

  return { ok: true, joined: response.data.result, response };
};

/**
 * Invite / attach collaborators to an active session.
 * POST /api/v1/sessions/:id/collaborators { user_ids: [...] }
 */
export const addSessionCollaborators = async (
  sessionId,
  userIds = [],
  { skipErrorToast = true } = {}
) => {
  const id = String(sessionId || '').trim();
  const ids = normalizeUserIds(userIds);

  if (!id) return { ok: false, reason: 'missing_session' };
  if (ids.length === 0) return { ok: false, reason: 'empty_users' };

  const response = await post(
    endpoints.sessionCollaborators(id),
    { user_ids: ids },
    { skipErrorToast }
  );

  if (!response?.data?.success) {
    return { ok: false, reason: 'api', response };
  }

  return { ok: true, result: response.data.result, response };
};

/**
 * Remove collaborators from a session.
 * DELETE /api/v1/sessions/:id/collaborators { user_ids: [...] }
 */
export const removeSessionCollaborators = async (
  sessionId,
  userIds = [],
  { skipErrorToast = true } = {}
) => {
  const id = String(sessionId || '').trim();
  const ids = normalizeUserIds(userIds);

  if (!id) return { ok: false, reason: 'missing_session' };
  if (ids.length === 0) return { ok: false, reason: 'empty_users' };

  const response = await del(endpoints.sessionCollaborators(id), {
    data: { user_ids: ids },
    skipErrorToast,
  });

  if (!response?.data?.success) {
    return { ok: false, reason: 'api', response };
  }

  return { ok: true, result: response.data.result, response };
};

/** Impression reactions on a session collaborator row. */
export const SESSION_COLLAB_REACTION = {
  like: '👍',
  heart: '❤️',
};

export const impressionFromReaction = reaction => {
  const emoji = String(reaction || '').trim();
  if (!emoji) return { liked: false, hearted: false, reaction: null };
  if (emoji === SESSION_COLLAB_REACTION.like || emoji === '👍') {
    return {
      liked: true,
      hearted: false,
      reaction: SESSION_COLLAB_REACTION.like,
    };
  }
  if (
    emoji === SESSION_COLLAB_REACTION.heart ||
    emoji === '❤' ||
    emoji === '♥️' ||
    emoji === '❤️'
  ) {
    return {
      liked: false,
      hearted: true,
      reaction: SESSION_COLLAB_REACTION.heart,
    };
  }
  return { liked: false, hearted: false, reaction: emoji };
};

/** Aggregate like/heart counts from reaction_summary or reaction rows. */
export const countsFromReactionSummary = summary => {
  let likeCount = 0;
  let heartCount = 0;
  (Array.isArray(summary) ? summary : []).forEach(row => {
    const count = Number(row?.count ?? 1);
    if (!Number.isFinite(count) || count <= 0) return;
    const impression = impressionFromReaction(row?.reaction || row?.emoji);
    if (impression.liked) likeCount += count;
    if (impression.hearted) heartCount += count;
  });
  return { likeCount, heartCount };
};

export const countsFromReactionRows = rows => {
  let likeCount = 0;
  let heartCount = 0;
  (Array.isArray(rows) ? rows : []).forEach(row => {
    const impression = impressionFromReaction(row?.reaction || row?.emoji);
    if (impression.liked) likeCount += 1;
    if (impression.hearted) heartCount += 1;
  });
  return { likeCount, heartCount };
};

export const emptyImpression = () => ({
  liked: false,
  hearted: false,
  reaction: null,
  likeCount: 0,
  heartCount: 0,
});

/**
 * GET /api/v1/sessions/:sessionId/collaborators/:collaboratorId/reactions
 */
export const getSessionCollaboratorReactions = async (
  sessionId,
  collaboratorId,
  { skipErrorToast = true } = {}
) => {
  const sid = String(sessionId || '').trim();
  const cid = String(collaboratorId || '').trim();
  if (!sid || !cid) return { ok: false, reason: 'missing_ids', rows: [] };

  const response = await get(
    endpoints.sessionCollaboratorReactions(sid, cid),
    { skipErrorToast }
  );

  if (!response?.data?.success) {
    return { ok: false, reason: 'api', response, rows: [] };
  }

  const result = response.data.result || {};
  const rows = Array.isArray(result.data)
    ? result.data
    : Array.isArray(result)
      ? result
      : [];

  return { ok: true, rows, result, response };
};

/**
 * POST reaction — body: { reaction: "👍" | "❤️" }
 */
export const addSessionCollaboratorReaction = async (
  sessionId,
  collaboratorId,
  reaction,
  { skipErrorToast = true } = {}
) => {
  const sid = String(sessionId || '').trim();
  const cid = String(collaboratorId || '').trim();
  const emoji = String(reaction || '').trim();
  if (!sid || !cid || !emoji) {
    return { ok: false, reason: 'missing_ids' };
  }

  const response = await post(
    endpoints.sessionCollaboratorReactions(sid, cid),
    { reaction: emoji },
    { skipErrorToast }
  );

  if (!response?.data?.success) {
    return { ok: false, reason: 'api', response };
  }

  return { ok: true, result: response.data.result, response };
};

/**
 * DELETE reaction — body: { reaction }
 */
export const removeSessionCollaboratorReaction = async (
  sessionId,
  collaboratorId,
  reaction,
  { skipErrorToast = true } = {}
) => {
  const sid = String(sessionId || '').trim();
  const cid = String(collaboratorId || '').trim();
  const emoji = String(reaction || '').trim();
  if (!sid || !cid || !emoji) {
    return { ok: false, reason: 'missing_ids' };
  }

  const response = await del(endpoints.sessionCollaboratorReactions(sid, cid), {
    data: { reaction: emoji },
    skipErrorToast,
  });

  if (!response?.data?.success) {
    return { ok: false, reason: 'api', response };
  }

  return { ok: true, result: response.data.result, response };
};

const normalizeTaskSessionStatus = value =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_');

/**
 * Active ongoing session on a task:
 * - has on_going_session_id
 * - on_going_session_status === "active"
 *
 * joined  → view (Session Joined / open session)
 * invited → continue (Join Session / join modal)
 * else    → start (Start Session)
 *
 * Always derive from live on_going_* fields so action labels stay correct
 * after join/leave without a full reload.
 */
export const resolveTaskSessionAction = task => {
  const sessionId = task?.onGoingSessionId || task?.on_going_session_id || null;
  const sessionStatus = normalizeTaskSessionStatus(
    task?.onGoingSessionStatus || task?.on_going_session_status
  );
  const userStatus = normalizeTaskSessionStatus(
    task?.onGoingSessionUserStatus || task?.on_going_session_user_status
  );

  const isActiveOngoing = Boolean(sessionId) && sessionStatus === 'active';
  if (!isActiveOngoing) return 'start';
  if (userStatus === 'joined') return 'view';
  if (userStatus === 'invited') return 'continue';
  return 'start';
};

export const taskSessionActionLabel = action => {
  if (action === 'view') return 'Session Joined';
  if (action === 'continue') return 'Join Session';
  return 'Start Session';
};

/** Patch a mapped task's ongoing-session fields and recompute action labels. */
export const withTaskOngoingSession = (
  task,
  { sessionId = null, sessionStatus = null, userStatus = null } = {}
) => {
  if (!task) return task;

  const next = {
    ...task,
    onGoingSessionId: sessionId,
    on_going_session_id: sessionId,
    onGoingSessionStatus: sessionStatus,
    on_going_session_status: sessionStatus,
    onGoingSessionUserStatus: userStatus,
    on_going_session_user_status: userStatus,
  };

  if (task.action === 'done' || task.completed || task.sessionAction === 'done') {
    return {
      ...next,
      sessionAction: 'done',
      action: 'done',
    };
  }

  const sessionAction = resolveTaskSessionAction(next);
  return {
    ...next,
    sessionAction,
    action: sessionAction === 'start' ? 'start' : 'continue',
  };
};

export const taskMatchesOngoingSession = (task, sessionId) => {
  if (!task || sessionId == null || sessionId === '') return false;
  const ongoing = task.onGoingSessionId || task.on_going_session_id || null;
  return Boolean(ongoing) && String(ongoing) === String(sessionId);
};
